import {
  isSessionDown,
  type ClinicHealth,
  type WaSession,
  type WaSessions,
} from "./api";

/**
 * Clinic health — the infrastructure the care team was blind to. It answers two
 * questions before a CM reads a single card:
 *   1. Is every WhatsApp number connected? (read-bot instances, write bots, doctor
 *      bot, Care Manager, ...) — a down session silently breaks a whole channel.
 *   2. Are doctor answers actually reaching parents, or stuck unsynced?
 * So it sits ABOVE the queue.
 *
 * DISPLAY-ONLY. Optional: sessions and the aggregate health each render nothing on
 * failure — we never claim "all connected" we cannot verify. If BOTH are absent the
 * whole strip disappears.
 */

/** Human label for a session from its type tags, falling back to the raw name. */
function sessionRole(s: WaSession): string {
  const t = s.type ?? [];
  if (t.includes("care") || t.includes("support")) return "Care / support";
  if (t.includes("doctor")) return "Doctor";
  if (t.includes("bn-doctors")) return "Write";
  if (t.includes("group-automation")) return "Read";
  return "Other";
}

function SessionChip({ s }: { s: WaSession }) {
  const down = isSessionDown(s);
  return (
    <span
      data-testid="health-session"
      data-session={s.session_name}
      data-state={down ? "down" : "up"}
      title={`${s.session_name} · ${sessionRole(s)} · ${s.phone_number} · ${
        s.status
      }`}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded px-1.5 py-0.5 text-xs ${
        down
          ? "font-semibold text-crit-600 ring-1 ring-crit-200 dark:text-red-400 dark:ring-navy-600"
          : "text-clinic-600 dark:text-gray-300"
      }`}
    >
      <span
        aria-hidden="true"
        className={`h-2 w-2 rounded-full ${
          down ? "bg-crit-600" : "bg-green-600 dark:bg-green-500"
        }`}
      />
      {s.session_name}
      {down && <span className="uppercase tracking-wide">down</span>}
    </span>
  );
}

/**
 * Is anything actually wrong? Healthy infra costs zero pixels (a header pill); broken
 * infra preempts the whole page (the banner below). "If a bot is down nothing else
 * matters" — so behave that way, instead of a permanent box that outranks a parent
 * who has been waiting 42 minutes.
 */
export function healthHasAlert(
  sessions: WaSessions | null,
  health: ClinicHealth | null
): boolean {
  return (
    (sessions?.down ?? 0) > 0 ||
    !!health?.undeliveredAlert ||
    !!health?.doctorReplyAlert
  );
}

/** The healthy state: one muted pill in the header row. Nothing to act on. */
export function ConnectionsPill({ sessions }: { sessions: WaSessions | null }) {
  if (!sessions) return null;
  return (
    <span
      data-testid="connections-pill"
      className="inline-flex items-center gap-1.5 whitespace-nowrap font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-clinic-500 dark:text-gray-400"
    >
      <span
        aria-hidden="true"
        className="h-1.5 w-1.5 rounded-full bg-green-600 dark:bg-green-500"
      />
      {sessions.connected}/{sessions.total} bots up
    </span>
  );
}

export default function ClinicHealthStrip({
  sessions,
  health,
}: {
  sessions: WaSessions | null;
  health: ClinicHealth | null;
}) {
  // Healthy (or nothing loaded) -> the header pill carries it; this banner is for
  // failures ONLY, so red on this page always means "something is broken".
  if (!healthHasAlert(sessions, health)) return null;

  const list = sessions?.sessions ?? [];
  // down first, so the thing that needs attention leads.
  const ordered = [...list].sort(
    (a, b) => Number(isSessionDown(b)) - Number(isSessionDown(a))
  );
  const anyDown = (sessions?.down ?? 0) > 0;
  const anyAlert = true;

  return (
    <section
      data-testid="worklist-clinic-health"
      data-alert={anyAlert ? "true" : "false"}
      aria-label="Clinic health"
      className={`mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border px-4 py-2.5 ${
        anyAlert
          ? "border-crit-200 bg-crit-50 dark:border-navy-700 dark:!bg-navy-800"
          : "border-clinic-200 bg-white dark:border-navy-700 dark:!bg-navy-800"
      }`}
    >
      <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-clinic-500 dark:text-gray-400">
        Connections
      </span>

      {sessions && (
        <span
          data-testid="health-connections-count"
          className={`text-xs font-semibold tabular-nums ${
            anyDown
              ? "text-crit-600 dark:text-red-400"
              : "text-clinic-600 dark:text-gray-300"
          }`}
        >
          {sessions.connected}/{sessions.total} up
        </span>
      )}

      <span className="flex flex-wrap items-center gap-x-1 gap-y-1">
        {ordered.map((s) => (
          <SessionChip key={s.session_name} s={s} />
        ))}
      </span>

      {(health?.undeliveredAlert || health?.doctorReplyAlert) && (
        <span className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-1">
          {health?.undeliveredAlert && (
            <span
              data-testid="health-undelivered"
              className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-crit-600 dark:text-red-400"
            >
              <span
                aria-hidden="true"
                className="h-2 w-2 rounded-full bg-crit-600"
              />
              Answers stuck undelivered
            </span>
          )}
          {health?.doctorReplyAlert && (
            <span
              data-testid="health-doctor-reply"
              className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-crit-600 dark:text-red-400"
            >
              <span
                aria-hidden="true"
                className="h-2 w-2 rounded-full bg-crit-600"
              />
              Doctor app · no reply 5 min
            </span>
          )}
        </span>
      )}
    </section>
  );
}
